import React from 'react';
import {IconCertificate} from '@tabler/icons-react';
import {useParams} from 'react-router-dom';
import {FeatureFlagEnum} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {FormViewDate} from 'src/app/forms/FormViewDate/FormViewDate';
import {FormViewRow} from 'src/app/forms/FormViewRow/FormViewRow';
import {itemAssignmentDocumentTitle} from 'src/app/item-tracker/itemAssignmentHelpers';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {
  useDownloadZfBootsVoucherMutation,
  useDownloadZfGlassesVoucherMutation,
  useGetIssuanceCertificatePdfMutation,
  useGetItemAssignmentQuery,
} from 'src/redux/queries/itemAssignment';
import {useDocumentDownload} from 'src/shared/hooks/useDocumentDownload';
import {useItemTrackerBreadcrumbs} from 'src/shared/hooks/useItemTrackerBreadcrumbs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';

import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';

const ItemTrackerIssueCertificatePage: React.FC = () => {
  useSetDocumentTitle(`${itemAssignmentDocumentTitle} - Issuance Certificate`);

  const {download} = useDocumentDownload();

  const {definitionId, id} = useParams();
  const assignmentId = id !== undefined ? +id : 0;

  const {data: item, isLoading} = useGetItemAssignmentQuery(assignmentId, {
    skip: id === undefined,
  });

  const [getCertificatePdf, {isLoading: isCertificateLoading}] =
    useGetIssuanceCertificatePdfMutation();
  const [triggerDownloadZfGlassesVoucher, {isLoading: isGlassesLoading}] =
    useDownloadZfGlassesVoucherMutation();
  const [triggerDownloadZfBootsVoucher, {isLoading: isBootsLoading}] =
    useDownloadZfBootsVoucherMutation();

  const handleDownload = () =>
    download(
      timeZone =>
        getCertificatePdf({id: assignmentId, timeZone}).unwrap(),
      `issuance-certificate-${item?.internalIdentifier ?? assignmentId}.pdf`
    );

  const handleVoucher = () => {
    const metadata = item?.itemDefinition?.metadata;
    if (metadata?.isZFGlassesVoucher === true) {
      return download(
        timeZone =>
          triggerDownloadZfGlassesVoucher({id: assignmentId, timeZone}).unwrap(),
        `glasses-voucher-${item?.internalIdentifier ?? assignmentId}.pdf`
      );
    }
    if (metadata?.isZFBootVoucher === true) {
      return download(
        timeZone =>
          triggerDownloadZfBootsVoucher({id: assignmentId, timeZone}).unwrap(),
        `boot-voucher-${item?.internalIdentifier ?? assignmentId}.pdf`
      );
    }
  };

  const hasVoucherOption =
    item?.itemDefinition?.metadata?.isZFGlassesVoucher === true ||
    item?.itemDefinition?.metadata?.isZFBootVoucher === true;

  const {breadcrumbs, fallbackTo} = useItemTrackerBreadcrumbs({
    mode: 'issue-certificate',
    definitionId,
    assignmentId: id,
    itemDefinitionName: item?.itemDefinition?.name,
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.ItemTracker}>
      {!isLoading ? (
        <>
          <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
          <PageHeader>
            <PageHeader.Title icon={<IconCertificate />}>
              {`${item?.itemDefinition.name} Issuance Certificate`}
            </PageHeader.Title>
            {item?.internalIdentifier && (
              <PageHeader.Meta>{item.internalIdentifier}</PageHeader.Meta>
            )}
            <PageHeader.Actions>
              <PageHeader.Action
                variant="export"
                onClick={handleDownload}
                loading={isCertificateLoading}>
                Download
              </PageHeader.Action>
              {hasVoucherOption && (
                <PageHeader.Action
                  onClick={handleVoucher}
                  loading={isGlassesLoading || isBootsLoading}>
                  Voucher
                </PageHeader.Action>
              )}
            </PageHeader.Actions>
          </PageHeader>

          <FormViewRow value={item?.assignedToUser?.name} label="Assigned To" />
          <FormViewRow value={item?.internalIdentifier} label="Voucher ID" />
          <FormViewRow value={item?.assignedByUser?.name} label="Assigned By" />
          <FormViewDate value={item?.assignedAt} label="Assignment Date" />
          <FormViewRow
            value={item?.internalIdentifier}
            label="Assignment Identifier"
          />

          <FormViewRow
            value={item?.approverSupervisorUser?.name}
            label="Supervisor Approval By"
          />
          <FormViewRow
            value={item?.approverAdminUser?.name}
            label="Administrator Approval By"
          />

          <FormViewRow value={item?.itemDefinition.name} label="Item Name" />
          <FormViewRow
            value={item?.itemDefinition.internalIdentifier}
            label="Item Identifier"
          />
          <FormViewRow value={item?.serial} label="Item Serial" />
          <FormViewRow value={item?.description} label="Item Description" />
        </>
      ) : (
        <TableBodyRender isLoading={isLoading} />
      )}
    </ContentWrapper>
  );
};

export default ItemTrackerIssueCertificatePage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ItemTrackerIssueCertificate(':definitionId', ':id'),
  element: <ItemTrackerIssueCertificatePage />,
  isPrivate: true,
});
